import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, shareReplay } from "rxjs";
import { Customer } from "../models/Customer";
import { ListItem } from "../models/ListItem";
import { LocDefault } from "../models/LocDefault";
import { LocationListItem } from "../models/LocationListItem";
import { LocationSearchParameters } from "../models/LocationSearchParameters";
import { PlanPeriodDates } from "../models/PlanPeriodDates";
import { EquipmentListItem } from "../models/EquipmentListItem";
import { SchedulingToolbarDefaults } from "../models/SchedulingToolbarDefaults";
import { UserItem } from "../models/UserItem";
import { UserItemForEmailQuestion } from "../models/UserItemForEmailQuestion";
import { UserSettings } from "../models/UserSettings";


@Injectable({
    providedIn: 'root'
})
export class ListDataService {

    private listItemsCache = new Map<string, Observable<ListItem[]>>();

    constructor(private http: HttpClient) {

    }

    getListItems(listType: string): Observable<ListItem[]> {
        if (!this.listItemsCache.has(listType)) {
            this.listItemsCache.set(listType,
                this.http.get<ListItem[]>('api/listitem/getlistitems/' + listType)
                    .pipe(shareReplay(1))
            );
        }
        return this.listItemsCache.get(listType)!;
    }

    getSchedulingToolbarDefaults(locCode: string): Observable<SchedulingToolbarDefaults> {
        return this.http.get<SchedulingToolbarDefaults>(`api/listitem/getschedulingtoolbardefaults/${locCode}`);
    }

    getGroupLocs(groupCode: string): Observable<ListItem[]> {
        return this.http.get<ListItem[]>('api/listitem/GetGroupLocs/' + groupCode);
    }

    getCommodities(locCode: string): Observable<ListItem[]> {
        return this.http.get<ListItem[]>('api/listitem/GetCommodities/' + locCode);
    }

    getLocation(locationId: number, locCode: string): Observable<LocationListItem> {
        return this.http.get<LocationListItem>('api/listitem/getlocation/' + locationId + '/' + locCode);
    }

    searchLocations(searchParms: LocationSearchParameters): Observable<LocationListItem[]> {
        return this.http.post<LocationListItem[]>('api/listitem/searchlocations', searchParms);
    }

    searchCustomers(searchParms: Customer): Observable<Customer[]> {
        return this.http.post<Customer[]>('api/listitem/searchcustomers', searchParms);
    }

    getPlanPeriods(locCode: string): Observable<ListItem[]> {
        return this.http.get<ListItem[]>('api/listitem/GetPlanPeriods/' + locCode);
    }

    getPeriodDates(planPeriod: string): Observable<PlanPeriodDates> {
        if (!planPeriod) {
            planPeriod = 'WeekCurrent';
        }
        return this.http.get<PlanPeriodDates>('api/listitem/getperioddates/' + planPeriod);
    }

    getWeekDay(date: string): Observable<string> {
        return this.http.get('api/listitem/GetWeekDay/' + date, { responseType: 'text' });
    }

    getTruckerRateReportUrl(): Observable<string> {
        return this.http.get('api/listitem/getTruckerRateReportUrl', { responseType: 'text' });
    }

    getLocDefaults(locCode: string, keys: string[]): Observable<LocDefault[]> {
        let params = new HttpParams().set('locCode', locCode);
        keys.forEach(x => {
            params = params.append('keys', x);
        });
        return this.http.get<LocDefault[]>(`api/listitem/getlocdefaults/`, { params });
    }

    getFavoriteTruckers(): Observable<Customer[]> {
        return this.http.get<Customer[]>('api/listitem/getfavoritetruckers');
    }

    addFavoriteTrucker(truckerId: number): Observable<boolean> {
        return this.http.post<boolean>('api/listitem/addfavoritetrucker/' + truckerId, {});
    }

    removeFavoriteTrucker(truckerId: number): Observable<boolean> {
        return this.http.delete<boolean>('api/listitem/removefavoritetrucker/' + truckerId);
    }

    getEnvironment(): Observable<string> {
        return this.http.get<string>('api/listitem/getenvironment/', { responseType: 'text' as 'json' });
    }

    getEquipmentTypes(): Observable<EquipmentListItem[]> {
        return this.http.get<EquipmentListItem[]>('api/listitem/getequipmenttypes');
    }
}